import { InteractionIconOverlayProps as InteractionIconOverlayProps } from "./types";
import { useInteractionStyle } from "@/components/organisms/interaction/style";
import {
	InteractionHandlingStatus,
	InteractionStatus,
} from "@/repository/models";
import { InteractionPanelItemType } from "@/components/organisms/interaction/types";
import { useCallback, useMemo } from "react";
import {
	CallSessionState,
	useCallSessionStore,
} from "@/common/stores/nonpersistingStores/callSession";
import { PopoverView } from "@/components/atoms/popover";
import { InteractionOverlay } from "@/components/atoms/interactionOverlay";
import { InteractionType } from "@/common/types";
import { mergeClasses } from "@fluentui/react-components";
import { getInteractionIcon } from "@/common/helpers/interactionIconHelper";

export const InteractionIconOverlay = (props: InteractionIconOverlayProps) => {
	const {
		interaction,
		itemPanelStatus,
		interactionHandlingStatus,
		isHighLighted,
		hideUnreadIcon: hideUnreadStatus,
		mountNode,
		isSkillBasedRoutingEnabled,
		hideDirectionArrow,
	} = props;

	const classes = useInteractionStyle();

	const callSession = useCallSessionStore(
		(state: CallSessionState) => state.callSession
	);

	const mapInteractionTypeAndStatusToPanelType = useCallback(
		(
			interactionType: InteractionType,
			interactionStatus: InteractionStatus
		) => {
			switch (interactionType) {
				case InteractionType.Generic:
					return InteractionPanelItemType.Generic;
				case InteractionType.Email:
					return getParkedInteractionPanelItemType(
						interactionStatus,
						InteractionPanelItemType.ParkedEmail,
						InteractionPanelItemType.Email
					);
				case InteractionType.Voicemail:
					return InteractionPanelItemType.Voicemail;
				case InteractionType.WhatsApp:
					return getParkedInteractionPanelItemType(
						interactionStatus,
						InteractionPanelItemType.ParkedWhatsApp,
						InteractionPanelItemType.WhatsApp
					);
				case InteractionType.Webchat:
					return getParkedInteractionPanelItemType(
						interactionStatus,
						InteractionPanelItemType.ParkedWebchat,
						InteractionPanelItemType.Webchat
					);
			}

			switch (interactionStatus) {
				case InteractionStatus.CallbackRequest:
				case InteractionStatus.CallbackOffering:
					return InteractionPanelItemType.Callback;
				case InteractionStatus.Parked:
					return InteractionPanelItemType.ParkedCall;
				default:
					return InteractionPanelItemType.Call;
			}
		},
		[]
	);

	const getParkedInteractionPanelItemType = (
		interactionStatus: InteractionStatus,
		parkStatus: InteractionPanelItemType,
		unparkedStatus: InteractionPanelItemType
	) => {
		return interactionStatus === InteractionStatus.Parked
			? parkStatus
			: unparkedStatus;
	};

	const mapInteractionTypeToDebuggingId = useMemo(() => {
		if (interaction.type == InteractionType.Call) {
			return interaction.sessionId ?? interaction.id;
		}
		return interaction.id;
	}, [interaction]);

	const hasUnreadMessages = (isSimplified: boolean) => {
		return (
			interactionHandlingStatus !== InteractionHandlingStatus.Waiting &&
			(interaction.type === InteractionType.Email ||
				interaction.type === InteractionType.WhatsApp ||
				interaction.type === InteractionType.Webchat) &&
			interaction.isLastMessageSeen === false &&
			!isSimplified
		);
	};

	const getHoverableInteractionIcon = useCallback(() => {
		const isMonitoredInteraction =
			(interaction.id === callSession?.id &&
				callSession.supervisorId !== null) ||
			interaction.isSupervised ||
			!!interaction.monitoringSupervisorId;

		const hasUnreadMessagesIcon = hasUnreadMessages(false);

		return (
			<PopoverView
				content={
					<>
						<InteractionOverlay
							item={{
								...interaction,
								type: mapInteractionTypeAndStatusToPanelType(
									interaction.type,
									interaction.status
								),
								statusOnPanel: itemPanelStatus,
								itemDebuggingId: mapInteractionTypeToDebuggingId,
								isSkillBasedRoutingEnabled: isSkillBasedRoutingEnabled,
							}}
						/>
					</>
				}
				positioning="above-start"
				trigger={
					<>
						{getInteractionIcon(interaction, {
							isMonitoredInteraction,
							isHighlighted: isHighLighted,
							hasUnreadIcon: !hideUnreadStatus && hasUnreadMessagesIcon,
							hideDirectionArrow,
						})}
					</>
				}
				renderOutsideTheDOM={mountNode?.current != null}
				openOnHover={true}
				mountNode={mountNode}
				mouseLeaveDelay={0}
				classes={{
					trigger: mergeClasses(
						classes.interactionListIcon,
						isHighLighted && classes.interactionListIconHighlighted
					),
					content: classes.interactionOverlay,
				}}
			/>
		);
	}, [
		callSession,
		isHighLighted,
		interaction,
		interactionHandlingStatus,
		itemPanelStatus,
	]);

	return getHoverableInteractionIcon();
};
